/** Image / registration fields shown in the left table (priority order). */
export const TABLE_COLUMN_PRIORITY = [
  'itemid',
  'type',
  'isic_id',
  'image_type',
  'fov_mm_x',
  'fov_mm_y',
  'x_min',
  'x_max',
  'y_min',
  'y_max',
  'slices',
  'z_um_bottom',
  'z_um_top',
  'yamlId',
  'yamlid',
];

/** Keys omitted from the table (shown in clinical panel or hidden). */
export const TABLE_EXCLUDED_KEYS = new Set([
  'a',
  'b',
  'c',
  'd',
  'description',
  'derm_to_macro_matrix',
  'age',
  'sex',
  'anatom_site',
  'anatomical_site',
  'diagnosis',
  'diagnosis_confirm_type',
  'melanocytic',
  'concomitant_biopsy',
  'patient_id',
  'lesion_id',
  'rcm_case_id',
  'image_manipulation',
]);

/** Clinical detail fields for the right panel. */
export const CLINICAL_FIELD_DEFS = [
  { key: 'patient_id', label: 'Patient ID', group: 'Case' },
  { key: 'lesion_id', label: 'Lesion ID', group: 'Case' },
  { key: 'rcm_case_id', label: 'RCM case ID', group: 'Case' },
  { key: 'age', label: 'Age', group: 'Demographics' },
  { key: 'sex', label: 'Sex', group: 'Demographics' },
  { key: 'anatom_site', label: 'Anatomical site', group: 'Demographics' },
  { key: 'anatomical_site', label: 'Anatomical site', group: 'Demographics' },
  { key: 'diagnosis', label: 'Diagnosis', group: 'Diagnosis' },
  { key: 'diagnosis_confirm_type', label: 'Diagnosis confirmation', group: 'Diagnosis' },
  { key: 'melanocytic', label: 'Melanocytic', group: 'Diagnosis' },
  { key: 'concomitant_biopsy', label: 'Concomitant biopsy', group: 'Diagnosis' },
  { key: 'image_manipulation', label: 'Image manipulation', group: 'Imaging' },
];

export const SELECTED_IMAGE_FIELD_DEFS = [
  { key: 'itemid', label: 'Item ID' },
  { key: 'type', label: 'Type' },
  { key: 'isic_id', label: 'ISIC ID' },
  { key: 'image_type', label: 'Image type' },
];

const HEADER_LABELS = {
  itemid: 'Item ID',
  fov_mm_x: 'FOV X (mm)',
  fov_mm_y: 'FOV Y (mm)',
  x_min: 'X Min',
  x_max: 'X Max',
  y_min: 'Y Min',
  y_max: 'Y Max',
  z_um_bottom: 'Z Bottom (μm)',
  z_um_top: 'Z Top (μm)',
  yamlid: 'YAML ID',
  yamlId: 'YAML ID',
  isic_id: 'ISIC ID',
  image_type: 'Image type',
  slices: 'Slices',
};

function normalizeKey(key) {
  return (key || '').toLowerCase();
}

export function formatRegistrationHeaderName(key) {
  const lower = normalizeKey(key);
  if (HEADER_LABELS[key] || HEADER_LABELS[lower]) {
    return HEADER_LABELS[key] || HEADER_LABELS[lower];
  }
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
}

export function isTableExcludedKey(key) {
  if (!key || !key.trim()) return true;
  const lower = normalizeKey(key);
  if (TABLE_EXCLUDED_KEYS.has(lower)) return true;
  if (lower.includes('timestamp')) return true;
  return false;
}

export function orderTableColumnKeys(allKeys) {
  const keys = Array.from(allKeys).filter((key) => !isTableExcludedKey(key));
  const priorityIndex = new Map(
    TABLE_COLUMN_PRIORITY.map((key, index) => [normalizeKey(key), index])
  );

  return keys.sort((a, b) => {
    const aIdx = priorityIndex.get(normalizeKey(a));
    const bIdx = priorityIndex.get(normalizeKey(b));
    if (aIdx != null && bIdx != null) return aIdx - bIdx;
    if (aIdx != null) return -1;
    if (bIdx != null) return 1;
    return a.localeCompare(b);
  });
}

function pickField(item, key) {
  if (!item) return null;
  if (item[key] != null && item[key] !== '') return item[key];
  const lower = normalizeKey(key);
  const match = Object.keys(item).find((k) => normalizeKey(k) === lower);
  return match ? item[match] : null;
}

export function pickRegistrationField(item, key) {
  const value = pickField(item, key);
  if (value == null || value === '' || value === 'null' || value === 'undefined') {
    return null;
  }
  return String(value);
}

function formatClinicalValue(value) {
  if (value == null || value === '' || value === 'null' || value === 'undefined') {
    return null;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

export function buildClinicalFieldGroups(selectedItem, folderMeta) {
  const groups = new Map();
  const seenLabels = new Set();

  const addField = (group, label, value) => {
    const formatted = formatClinicalValue(value);
    if (formatted == null) return;
    const dedupeKey = `${group}:${label}`;
    if (seenLabels.has(dedupeKey)) return;
    seenLabels.add(dedupeKey);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push({ label, value: formatted });
  };

  CLINICAL_FIELD_DEFS.forEach(({ key, label, group }) => {
    const fromItem = pickField(selectedItem, key);
    const fromFolder = pickField(folderMeta, key);
    addField(group, label, fromItem ?? fromFolder);
  });

  if (selectedItem) {
    SELECTED_IMAGE_FIELD_DEFS.forEach(({ key, label }) => {
      addField('Selected image', label, pickField(selectedItem, key));
    });
  }

  return Array.from(groups.entries())
    .map(([title, fields]) => ({ title, fields }))
    .filter((group) => group.fields.length > 0);
}

export function getClinicalSourceItem(registrationData, selectedIndex) {
  if (!Array.isArray(registrationData) || registrationData.length === 0) {
    return null;
  }
  if (selectedIndex != null && registrationData[selectedIndex]) {
    return registrationData[selectedIndex];
  }
  return registrationData.find((row) => pickField(row, 'diagnosis')) || registrationData[0];
}
